import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const MyTracksModal = ({ isOpen, onClose }) => {
  const [userTracks, setUserTracks] = useState([]);
  const [tracksLoading, setTracksLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchUserTracks();
    }
  }, [isOpen]);

  const fetchUserTracks = async () => {
    try {
      setTracksLoading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      if (!token) {
        setUserTracks([]);
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/forum/audio/user-tracks`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserTracks(data.tracks || []);
      } else {
        console.error('Failed to fetch user tracks');
        setUserTracks([]);
      }
    } catch (error) {
      console.error('Error fetching user tracks:', error);
      setUserTracks([]);
    } finally {
      setTracksLoading(false);
    }
  };

  const deleteUserTrack = async (trackId) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('beatseek_token');
      
      const response = await fetch(`${BACKEND_URL}/api/forum/audio/${trackId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Refresh tracks list
        fetchUserTracks();
        alert('Track deleted successfully!');
      } else {
        alert('Failed to delete track. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting track:', error);
      alert('Error deleting track. Please try again.');
    }
  };

  const formatTimeRemaining = (track) => {
    if (track.expired) return 'Expired';
    if (track.days_remaining > 0) return `${track.days_remaining}d remaining`;
    if (track.hours_remaining > 0) return `${track.hours_remaining}h remaining`;
    return 'Expires soon';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 rounded-2xl border border-purple-500/20 w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white">My Track Previews</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors duration-200"
          >
            <X size={24} />
          </button>
        </div>

        {/* Quota Summary */}
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-white font-semibold">Storage Usage</h4>
            <span className="text-sm text-gray-400">{userTracks.length}/2 slots used</span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all duration-500 ${
                userTracks.length >= 2 ? 'bg-red-500' : userTracks.length >= 1 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{width: `${(userTracks.length / 2) * 100}%`}}
            />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Track previews automatically expire after 7 days. You can have maximum 2 active tracks.
          </p>
        </div>

        {/* Tracks List */}
        <div className="space-y-4">
          {tracksLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
          ) : userTracks.length > 0 ? (
            userTracks.map((track) => (
              <div key={track.id} className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                      </svg>
                      <h5 className="font-semibold text-white">{track.original_filename}</h5>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        track.expired 
                          ? 'bg-red-500/20 text-red-300 border border-red-500/50'
                          : track.days_remaining <= 1
                            ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/50'
                            : 'bg-green-500/20 text-green-300 border border-green-500/50'
                      }`}>
                        {formatTimeRemaining(track)}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-400 mb-3">
                      <div className="flex items-center gap-4">
                        <span>Size: {track.file_size_mb}MB</span>
                        <span>Uploaded: {new Date(track.created_at).toLocaleDateString()}</span>
                      </div>
                      {track.post_info && (
                        <div className="mt-1">
                          <span className="text-purple-400">Post: "{track.post_info.title}"</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Audio Player for active tracks */}
                    {!track.expired && (
                      <div className="bg-gray-700/50 rounded-lg p-3 mb-3">
                        <audio 
                          controls 
                          className="w-full"
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/forum/audio/${track.id}/play`}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="ml-4">
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete "${track.original_filename}"? This action cannot be undone.`)) {
                          deleteUserTrack(track.id);
                        }
                      }}
                      className="text-red-400 hover:text-red-300 p-2 rounded-lg hover:bg-red-500/20 transition-colors duration-300"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <svg className="w-16 h-16 mx-auto text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
              <h4 className="text-lg font-semibold text-white mb-2">No active track previews</h4>
              <p className="text-gray-400 mb-4">Upload your first track preview to get community feedback!</p>
              <p className="text-sm text-gray-500">
                Go to Social → Producers Corner → Forum → Track Feedback to upload tracks.
              </p>
            </div>
          )}
        </div>

        {/* Close button */}
        <div className="flex justify-end mt-6 pt-4 border-t border-gray-700/50">
          <button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition-colors duration-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyTracksModal;